import styled from '@emotion/styled';
import { type ReactNode } from 'react';

import { SpinnerLoading } from '../../common/SpinnerLoading';

import { theme } from '../../../theme/theme';

import { MemoizedStreamVideoView } from './StreamVideoView';

export interface LiveVideoProps {
  video: { id: string };
  playing: boolean;
  includeAudio: boolean;
  backgroundImage?: string;
  errorOverlay?: ReactNode;
  pointerEvents?: 'auto' | 'none';
  stream: MediaStream | null;
  isStreamConnected: boolean;
}

export function LiveVideo(props: LiveVideoProps) {
  const shouldShowSpinner = props.playing && !props.isStreamConnected && !props.errorOverlay;

  return (
    <S.Wrapper style={{ pointerEvents: props.errorOverlay ? 'none' : props.pointerEvents }}>
      {props.backgroundImage && (
        <>
          <S.BackgroundImage src={props.backgroundImage} />
          <S.BackgroundOverlay />
        </>
      )}
      {props.errorOverlay && <S.ErrorOverlay>{props.errorOverlay}</S.ErrorOverlay>}
      <S.VideoContent style={{ opacity: props.isStreamConnected && !props.errorOverlay ? 1 : 0 }}>
        {props.playing && props.stream && (
          <MemoizedStreamVideoView
            key={`stream-${props.video.id}`}
            stream={props.stream}
            includeAudio={props.includeAudio}
            pointerEvents={props.pointerEvents}
          />
        )}
      </S.VideoContent>
      {shouldShowSpinner && (
        <S.SpinnerContainer>
          <SpinnerLoading size={40} color={theme.color.white} rainbow={false} />
        </S.SpinnerContainer>
      )}
    </S.Wrapper>
  );
}

function S() {}
LiveVideo.S = S;

S.Wrapper = styled.div`
  flex: 1;
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  height: 100%;
`;

S.BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`;

S.BackgroundOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 2;
`;

S.VideoContent = styled.div`
  flex: 1;
  position: relative;
  z-index: 3;
  background-color: transparent;
  display: flex;
`;

S.ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
`;

S.SpinnerContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  background-color: transparent;
`;
